// Totals the engine calculated from the records (QueryResult.totals), read by grouping rather than by level name.
//
// The server sends `[{ level: 'row' | 'column' | 'grand', values: { alias: value } }]`: each entry's values hold its
// measures plus the dimensions it is grouped by; rolled-up dimensions are absent. A currency break-down is never
// rolled up, so a money total keeps its currency dimension and a grand total then has one entry per currency.
// Tables and pivots ask for "the total of these groups" (a set of grouping fields and their values); an entry
// answers only when it is grouped by exactly those fields plus the dimensions the engine never rolls up. Anything
// the server did not send falls back to client totals, and only for measures whose group values can be combined.

import type { QueryDisplay, QueryResult, QueryResultColumn } from './types';
import { combineMeasure, displayCell, isCombinable } from './format';

type Values = Record<string, unknown>;

const typedKey = (value: unknown): string => JSON.stringify([typeof value, value ?? null]);

export interface ServerTotals {
    /** Dimensions every total keeps (the currency break-down); empty when everything rolls up. */
    kept: QueryResultColumn[];
    /** The grand total entries: one, or one per currency. */
    grand: Values[];
    /**
     * Entries totalling the group where `fields` take `values` (kept dimensions vary freely). Null when the server
     * sent no totals at this grouping, so the caller falls back to its own.
     */
    find(fields: QueryResultColumn[], values: unknown[]): Values[] | null;
}

/** Read `result.totals` in the engine's shape; null when the result carries none. */
export function serverTotals(result: QueryResult): ServerTotals | null {
    const raw = (result as { totals?: unknown }).totals;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const dims = result.columns.filter(c => c.kind === 'dimension');
    const entries = raw.filter((t): t is { level?: unknown; values: Values } => !!t && typeof t === 'object' && !!(t as { values?: unknown }).values
        && typeof (t as { values?: unknown }).values === 'object');
    if (!entries.length) return null;
    const signature = (aliases: Iterable<string>) => [...aliases].join('\u0000');
    const presentOf = (values: Values) => dims.filter(d => Object.prototype.hasOwnProperty.call(values, d.alias)).map(d => d.alias);
    const grand = entries.filter(e => e.level === 'grand').map(e => e.values);
    const keptAliases = new Set(grand.length ? presentOf(grand[0]) : []);
    const kept = dims.filter(d => keptAliases.has(d.alias));
    const bySignature = new Map<string, Values[]>();
    for (const entry of entries) {
        const key = signature(presentOf(entry.values));
        const list = bySignature.get(key) ?? []; list.push(entry.values); bySignature.set(key, list);
    }
    return {
        kept, grand,
        find(fields, values) {
            const wanted = new Set([...fields.map(f => f.alias), ...keptAliases]);
            const candidates = bySignature.get(signature(dims.filter(d => wanted.has(d.alias)).map(d => d.alias)));
            if (!candidates) return null;
            const matches = candidates.filter(entry => fields.every((f, i) => typedKey(entry[f.alias]) === typedKey(values[i])));
            return matches.length ? matches : null;
        },
    };
}

export interface TotalCell {
    /** False when no honest total can be shown (partial result, average across groups, several currencies of text money). */
    known: boolean;
    text: string;
    /** No records fall in this group (a pivot subtotal under a column its rows never reach): nothing to total. */
    empty?: boolean;
}

export interface TotalOptions {
    display?: QueryDisplay | null;
    /** Client totals are allowed (the result is complete). */
    clientTotals?: boolean;
    /** False for dataset measures whose group values must not be added again. Defaults to every measure. */
    reaggregable?: (measure: QueryResultColumn) => boolean;
}

/** Whether the client may total this measure from group values. */
export function clientCombinable(measure: QueryResultColumn, options: TotalOptions = {}): boolean {
    return isCombinable(measure) && (options.reaggregable?.(measure) ?? true);
}

/**
 * The text of one total. `server` are the matching engine entries (null: not sent); `records` are the rows the
 * total covers, used only when the server sent nothing and the measure can be combined.
 */
export function totalCell(measure: QueryResultColumn, server: Values[] | null, records: Values[], options: TotalOptions = {}): TotalCell {
    const display = options.display ?? null;
    const unknown: TotalCell = { known: false, text: '' };
    if (server && server.length && server.some(entry => measure.alias in entry)) {
        const withValue = server.filter(entry => measure.alias in entry);
        if (withValue.length === 1) {
            const [entry] = withValue;
            // A total spanning several currencies of text-stored money has no single value.
            if (entry[measure.alias] == null && measure.currencyAlias) return unknown;
            return { known: true, text: displayCell(entry[measure.alias], measure, display, { row: entry }) };
        }
        // One entry per currency: money is listed per currency, never added; counts and other numbers combine.
        if (measure.currencyAlias) {
            if (withValue.some(entry => entry[measure.alias] == null)) return unknown;
            return { known: true, text: withValue.map(entry => displayCell(entry[measure.alias], measure, display, { row: entry })).join(' · ') };
        }
        if (!clientCombinable(measure, options)) return unknown;
        return { known: true, text: displayCell(combineMeasure(measure, withValue.map(e => e[measure.alias])), measure, display, { row: withValue[0] }) };
    }
    // The engine sends a total for every group that has records; in a complete result a group with none has nothing to total.
    if (!records.length && options.clientTotals !== false) return { known: true, text: '', empty: true };
    if (options.clientTotals === false || !clientCombinable(measure, options)) return unknown;
    // Money in several currencies is never added on the client either.
    if (measure.currencyAlias) {
        const codes = new Set(records.map(r => typedKey(r[measure.currencyAlias!])));
        if (codes.size > 1) return unknown;
    }
    return { known: true, text: displayCell(combineMeasure(measure, records.map(r => r[measure.alias])), measure, display, { row: records[0] }) };
}
